/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { useEffect, useRef, useState } from 'react';

import { cssMap, jsx } from '@atlaskit/css';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { PARAMS } from './PARAMS';

const styles = cssMap({
	staticScribble: {
		display: 'inline-flex',
		alignItems: 'center',
		// inherits the color CSS property set on scribbleContainer — near-black in light, near-white in dark
		backgroundColor: 'currentcolor',
		borderRadius: token('radius.xsmall'),
		height: '1.125rem',
		aspectRatio: '2.8 / 1',
		verticalAlign: 'middle',
	},
	scribbleContainer: {
		// opposite of text: near-black in light mode, near-white in dark mode
		color: token('color.text'),
		position: 'relative',
		display: 'inline-flex',
		alignItems: 'center',
		height: '1.125rem',
		aspectRatio: '2.8 / 1',
		verticalAlign: 'middle',
		cursor: 'default',
	},
	scribbleSvg: {
		position: 'absolute',
		inset: token('space.0'),
		overflow: 'visible',
		pointerEvents: 'none',
	},
});

const EFFECTIVE_STROKE_WIDTH = Math.max(20 * PARAMS.strokeWidth, 6); // 8px
const REFERENCE_LINE_HEIGHT = 20; // px — baseline for strokeCount scaling

/**
 * Generates cubic-bezier SVG path strings covering the span area.
 * Odd-indexed strokes are reversed (right→left) to produce a back-and-forth pen motion.
 */
function generateStrokes(width: number, height: number): string[] {
	const spreadStart = (height * (1 - PARAMS.spread)) / 2;
	const spreadHeight = height * PARAMS.spread;
	const count = Math.max(Math.round(PARAMS.strokeCount * (height / REFERENCE_LINE_HEIGHT)), 1);

	return Array.from({ length: count }, (_, i) => {
		const t = count === 1 ? 0.5 : i / (count - 1);
		const yBase = spreadStart + t * spreadHeight;
		const jitterOffset = (Math.random() * 2 - 1) * PARAMS.jitter;
		const y = Math.max(spreadStart, Math.min(spreadStart + spreadHeight, yBase + jitterOffset));

		const cx1y = y + (Math.random() * 2 - 1) * PARAMS.wiggle;
		const cx2y = y + (Math.random() * 2 - 1) * PARAMS.wiggle;

		// Alternate direction: even strokes go left→right, odd strokes go right→left.
		const leftToRight = i % 2 === 0;
		const x0 = leftToRight ? 0 : width;
		const x3 = leftToRight ? width : 0;
		const cx1x = leftToRight
			? width * 0.3 + (Math.random() - 0.5) * width * 0.15
			: width * 0.7 + (Math.random() - 0.5) * width * 0.15;
		const cx2x = leftToRight
			? width * 0.7 + (Math.random() - 0.5) * width * 0.15
			: width * 0.3 + (Math.random() - 0.5) * width * 0.15;

		return `M ${x0.toFixed(2)} ${y.toFixed(2)} C ${cx1x.toFixed(2)} ${cx1y.toFixed(2)} ${cx2x.toFixed(2)} ${cx2y.toFixed(2)} ${x3.toFixed(2)} ${y.toFixed(2)}`;
	});
}

/**
 * Handles container measurement and stroke animation for a single scribble span.
 * Returns refs for the container and path elements alongside the derived path data and dimensions.
 */
function useScribbleAnimation(activationDelay: number): {
	containerRef: React.RefObject<HTMLSpanElement>;
	pathRefs: React.MutableRefObject<(SVGPathElement | null)[]>;
	pathData: string[];
	svgDims: { w: number; h: number } | null;
} {
	const containerRef = useRef<HTMLSpanElement>(null);
	const pathRefs = useRef<(SVGPathElement | null)[]>([]);
	const [pathData, setPathData] = useState<string[]>([]);
	const [svgDims, setSvgDims] = useState<{ w: number; h: number } | null>(null);

	// Measure the container and generate stroke paths after first paint.
	useEffect(() => {
		const el = containerRef.current;
		if (!el) {
			return;
		}
		const rafId = requestAnimationFrame(() => {
			const { width, height } = el.getBoundingClientRect();
			const w = width || el.offsetWidth;
			const h = height || el.offsetHeight;
			if (w > 0 && h > 0) {
				setSvgDims({ w, h });
				setPathData(generateStrokes(w, h));
			}
		});
		return () => cancelAnimationFrame(rafId);
	}, []);

	// Animate strokes via strokeDashoffset once paths are in the DOM.
	useEffect(() => {
		if (!pathData.length || !svgDims) {
			return;
		}

		let setRafId: number;
		let animRafId: number;
		let timerId: ReturnType<typeof setTimeout>;

		// One rAF to let React flush the new <path> elements to the DOM.
		setRafId = requestAnimationFrame(() => {
			const paths = pathRefs.current.filter((p): p is SVGPathElement => p !== null);
			if (!paths.length) {
				return;
			}

			// Cache path lengths and initialise to fully hidden.
			const lengths = paths.map((p) => p.getTotalLength());
			paths.forEach((p, i) => {
				p.style.strokeDasharray = String(lengths[i]);
				p.style.strokeDashoffset = String(lengths[i]);
			});

			timerId = setTimeout(() => {
				// Each stroke draws sequentially — one at a time, like a pen scribbling.
				const strokeDuration = PARAMS.duration / paths.length;
				const startTime = performance.now();

				const tick = (now: number) => {
					const elapsed = now - startTime;
					let stillAnimating = false;

					paths.forEach((p, i) => {
						const strokeStart = i * strokeDuration;
						const strokeElapsed = elapsed - strokeStart;

						if (strokeElapsed < 0) {
							// Not yet started — keep hidden.
							p.style.strokeDashoffset = String(lengths[i]);
							stillAnimating = true;
						} else if (strokeElapsed >= strokeDuration) {
							// Finished — fully drawn.
							p.style.strokeDashoffset = '0';
						} else {
							// Actively drawing — ease-out quadratic: progress = 1 - (1 - t)²
							const t = strokeElapsed / strokeDuration;
							const progress = 1 - (1 - t) ** 2;
							p.style.strokeDashoffset = String(lengths[i] * (1 - progress));
							stillAnimating = true;
						}
					});

					if (stillAnimating) {
						animRafId = requestAnimationFrame(tick);
					}
				};
				animRafId = requestAnimationFrame(tick);
			}, activationDelay);
		});

		return () => {
			cancelAnimationFrame(setRafId);
			// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
			clearTimeout(timerId);
			// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
			cancelAnimationFrame(animRafId);
		};
	}, [pathData, svgDims, activationDelay]);

	return { containerRef, pathRefs, pathData, svgDims };
}

const ScribbleOverlay = ({
	pathData,
	svgDims,
	pathRefs,
}: {
	pathData: string[];
	svgDims: { w: number; h: number };
	pathRefs: React.MutableRefObject<(SVGPathElement | null)[]>;
}): React.JSX.Element => (
	<svg
		css={styles.scribbleSvg}
		width={svgDims.w}
		height={svgDims.h}
		viewBox={`0 0 ${svgDims.w} ${svgDims.h}`}
		aria-hidden
	>
		{pathData.map((d, i) => (
			<path
				key={i}
				ref={(el) => {
					pathRefs.current[i] = el;
				}}
				d={d}
				fill="none"
				stroke="currentColor"
				strokeLinecap="round"
				// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- strokeWidth is a computed constant
				style={{ strokeWidth: EFFECTIVE_STROKE_WIDTH }}
			/>
		))}
	</svg>
);

const ScribbleSpan = ({
	ariaLabel,
	activationDelay,
}: {
	ariaLabel: string;
	activationDelay: number;
}): React.JSX.Element => {
	const { containerRef, pathRefs, pathData, svgDims } = useScribbleAnimation(activationDelay);

	return (
		<span ref={containerRef} css={styles.scribbleContainer} role="img" aria-label={ariaLabel}>
			{svgDims && <ScribbleOverlay pathData={pathData} svgDims={svgDims} pathRefs={pathRefs} />}
		</span>
	);
};

const StaticScribble = ({ ariaLabel }: { ariaLabel: string }): React.JSX.Element => (
	<Box as="span" xcss={styles.staticScribble} role="img" aria-label={ariaLabel} />
);

export const RedactedToken = ({
	ariaLabel,
	delay,
}: {
	ariaLabel: string;
	delay: number;
}): React.JSX.Element =>
	fg('rovo-guard-redaction-scribble-animation') ? (
		<ScribbleSpan ariaLabel={ariaLabel} activationDelay={delay} />
	) : (
		<StaticScribble ariaLabel={ariaLabel} />
	);
